import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
  Box,
  Chip,
  ListSubheader,
  Collapse,
  Autocomplete,
} from '@mui/material';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';
import Modal from '../Modal';
import { PrimaryButton, SecondaryButton } from '../ui';
import MenuItemImageUpload from '../MenuItemImageUpload';
import { IMAGE_GATEWAY_URL } from '../../config/images';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { CURRENCY } from '../../config/currency';
import { useCountryConfig } from '../../hooks/useCountryConfig';
import { isSingleTax, singleTaxLabel } from '../../config/tax';
import { financeApi } from '../../api/financeApi';
import toast from 'react-hot-toast';
import { todayLocalIso } from '../../utils/dateLocal';
import PaymentModeSelect from './PaymentModeSelect';

// --- Types ---

export interface ExpenseCategory {
  id: number;
  name: string;
  type: string; // 'EXPENSE' | 'STAFF' etc.
  isStaffCategory?: boolean;
  icon?: string;
}

export interface StaffMember {
  id: number;
  name: string;
  role?: string;
}

export interface ExpenseFormData {
  id?: number;
  categoryId: number | '';
  amount: number | '';
  vatAmount?: number | '';
  fromDate: string;        // required (was: date)
  toDate?: string;         // optional — leave blank for single day
  recordedDate?: string;   // optional — defaults to today
  date?: string;           // legacy: kept so we can hydrate from existing rows
  paymentStatus: 'PAID' | 'UNPAID' | 'PARTIALLY_PAID';
  paymentMode: string;
  supplierName: string;
  dueDate: string;
  staffMemberId: number | '';
  salaryMonth?: string; // "YYYY-MM" — for staff-category expenses, the payroll period
  description: string;
  imageKey?: string;
  imageUrl?: string;
  cgst?: number | '';
  sgst?: number | '';
  igst?: number | '';
}

export interface ExpenseFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ExpenseFormData) => Promise<void>;
  expense?: ExpenseFormData | null;
  categories: ExpenseCategory[];
  staffMembers?: StaffMember[];
  loading?: boolean;
  onCategoryCreated?: () => void;
}

// --- Constants ---

const PAYMENT_STATUSES = [
  { value: 'PAID', label: 'Paid' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
];

const STAFF_CATEGORY_NAMES = ['Salary', 'Advance', 'Bonus', 'Overtime', 'Deduction', 'Staff Meal', 'Staff Welfare'];

const getDefaultForm = (): ExpenseFormData => {
  const today = todayLocalIso();
  return ({
  categoryId: '',
  amount: '',
  vatAmount: '',
  fromDate: today,
  toDate: '',
  recordedDate: today,
  paymentStatus: 'PAID',
  paymentMode: 'CASH',
  supplierName: '',
  dueDate: '',
  staffMemberId: '',
  salaryMonth: '',
  description: '',
  cgst: '',
  sgst: '',
  igst: '',
});
};

const currentYearMonth = (): string => new Date().toISOString().slice(0, 7);

// --- Component ---

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  open,
  onClose,
  onSave,
  expense,
  categories,
  staffMembers = [],
  loading: externalLoading = false,
  onCategoryCreated,
}) => {
  const { theme } = useTheme();
  const { taxSublabels, taxLabel, formatAmount } = useCountryConfig();
  const singleTax = isSingleTax(taxSublabels);
  const { user } = useAuth();
  const isFinanceOnlyRestricted = Boolean(user?.financeOnlySteward);
  const [form, setForm] = useState<ExpenseFormData>(getDefaultForm());
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Recurring/Repeat state
  type RepeatFrequency = 'NONE' | 'WEEKLY' | 'BI_WEEKLY' | 'MONTHLY';
  const [repeatFrequency, setRepeatFrequency] = useState<RepeatFrequency>('NONE');
  const [repeatDay, setRepeatDay] = useState<number>(1);
  const [showRepeat, setShowRepeat] = useState(false);

  // Supplier autocomplete
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const [ruleHint, setRuleHint] = useState<{ categoryName: string; categoryId: number } | null>(null);
  const [transactionRules, setTransactionRules] = useState<Array<{ pattern: string; mappedType: string; mappedCategoryId: number | null; mappedCategoryName: string | null }>>([]);

  const isEdit = Boolean(expense?.id);

  // Fetch supplier options and rules when modal opens
  useEffect(() => {
    if (open) {
      financeApi.getUniqueSuppliers().then((res) => {
        setSupplierOptions(Array.isArray(res.data) ? res.data : []);
      }).catch(() => {/* ignore */});
      financeApi.getTransactionRules().then((res) => {
        setTransactionRules(Array.isArray(res.data) ? res.data : []);
      }).catch(() => {/* ignore */});
    }
  }, [open]);

  // Reset form when opening
  useEffect(() => {
    if (open) {
      if (expense) {
        // Map legacy `date` → `fromDate` for back-compat with existing rows
        // that haven't been re-saved with the new fields yet.
        const merged: ExpenseFormData = { ...getDefaultForm(), ...expense };
        merged.fromDate = expense.fromDate || expense.date || merged.fromDate;
        merged.toDate = expense.toDate ?? '';
        merged.recordedDate = expense.recordedDate || merged.recordedDate;
        setForm(merged);
        // Auto-expand "more details" if optional fields have values
        const hasOptionalValues =
          expense.supplierName || expense.dueDate || expense.staffMemberId || expense.description;
        setShowMore(Boolean(hasOptionalValues));
      } else {
        setForm(getDefaultForm());
        setShowMore(false);
      }
      setErrors({});
      setRuleHint(null);
      // Reset repeat state
      setRepeatFrequency('NONE');
      setRepeatDay(1);
      setShowRepeat(false);
    }
  }, [open, expense]);

  // Group categories: staff categories separate from general
  const { generalCategories, staffCategories } = useMemo(() => {
    const general: ExpenseCategory[] = [];
    const staff: ExpenseCategory[] = [];
    categories.forEach((cat) => {
      if (cat.isStaffCategory || STAFF_CATEGORY_NAMES.includes(cat.name)) {
        staff.push({ ...cat, isStaffCategory: true });
      } else {
        general.push(cat);
      }
    });
    return { generalCategories: general, staffCategories: staff };
  }, [categories]);

  // Determine if selected category is staff-related
  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === form.categoryId),
    [categories, form.categoryId]
  );
  const isStaffCategory = useMemo(
    () =>
      selectedCategory
        ? selectedCategory.isStaffCategory || STAFF_CATEGORY_NAMES.includes(selectedCategory.name)
        : false,
    [selectedCategory]
  );

  // Show due date for unpaid/partially paid
  const showDueDate = form.paymentStatus === 'UNPAID' || form.paymentStatus === 'PARTIALLY_PAID';

  // Payment mode is only meaningful when money actually moved. For UNPAID payables
  // it's just a credit/IOU, so hide the picker and silently coerce to CREDIT.
  const showPaymentMode = form.paymentStatus !== 'UNPAID';
  useEffect(() => {
    if (form.paymentStatus === 'UNPAID' && form.paymentMode !== 'CREDIT') {
      setForm((prev) => ({ ...prev, paymentMode: 'CREDIT' }));
    }
  }, [form.paymentStatus, form.paymentMode]);

  // --- Handlers ---

  const handleSupplierChange = useCallback((supplierName: string) => {
    setForm((prev) => ({ ...prev, supplierName }));
    // Check transaction rules for a match
    if (supplierName.trim() && transactionRules.length > 0) {
      const upperVal = supplierName.toUpperCase();
      const match = transactionRules.find(
        (r) => r.mappedType === 'EXPENSE' && r.mappedCategoryId && upperVal.includes(r.pattern.toUpperCase())
      );
      if (match && match.mappedCategoryId && match.mappedCategoryName) {
        setRuleHint({ categoryName: match.mappedCategoryName, categoryId: match.mappedCategoryId });
      } else {
        setRuleHint(null);
      }
    } else {
      setRuleHint(null);
    }
  }, [transactionRules]);

  const handleChange = (field: keyof ExpenseFormData, value: unknown) => {
    setForm((prev) => {
      const next: ExpenseFormData = { ...prev, [field]: value as ExpenseFormData[typeof field] };
      // When switching to a staff category, default salaryMonth to current month if blank.
      if (field === 'categoryId') {
        const cat = categories.find((c) => c.id === value);
        const isStaff = cat
          ? cat.isStaffCategory || STAFF_CATEGORY_NAMES.includes(cat.name)
          : false;
        if (isStaff && !next.salaryMonth) {
          next.salaryMonth = currentYearMonth();
        }
      }
      return next;
    });
    // Clear field error on change
    if (errors[field]) {
      setErrors((prev) => {
        const nextErr = { ...prev };
        delete nextErr[field];
        return nextErr;
      });
    }
    // Clear rule hint if category changes
    if (field === 'categoryId') {
      setRuleHint(null);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.categoryId) newErrors.categoryId = 'Category is required';
    if (!form.amount || Number(form.amount) <= 0) newErrors.amount = 'Enter a valid amount';
    if (!form.fromDate) newErrors.fromDate = 'From date is required';
    if (form.toDate && form.fromDate && form.toDate < form.fromDate) {
      newErrors.toDate = 'To date must be on or after From date';
    }
    if (isStaffCategory && !form.salaryMonth) {
      newErrors.salaryMonth = 'Pick the month this payment is for';
    }
    if (form.salaryMonth && !/^\d{4}-(0[1-9]|1[0-2])$/.test(form.salaryMonth)) {
      newErrors.salaryMonth = 'Salary month must be in YYYY-MM format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form);

      // Create recurring template if frequency is set (only for new expenses)
      if (!isEdit && repeatFrequency !== 'NONE') {
        try {
          await financeApi.createRecurringTemplate({
            categoryId: form.categoryId,
            amount: Number(form.amount),
            frequency: repeatFrequency,
            repeatDay,
            supplierName: form.supplierName || null,
            description: form.description || null,
            paymentMode: form.paymentMode,
          });
          toast.success('Recurring expense set up');
        } catch {
          toast.error('Expense saved, but recurring setup failed');
        }
      }

      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save expense';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // --- Shared style helpers ---

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components?.input?.focus || theme.primary },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
  };

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: theme.components?.input?.background || '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.border || '#DDD8D2' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.focus || theme.primary },
    '& .MuiSelect-select': { color: theme.text },
  };

  // --- Render ---

  const footer = (
    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
      <SecondaryButton onClick={onClose} disabled={saving} sx={{ borderRadius: '10px', px: 3, minHeight: 44 }}>
        Cancel
      </SecondaryButton>
      <PrimaryButton
        onClick={handleSubmit}
        disabled={saving || externalLoading}
        loading={saving}
        data-testid="expense-submit-button"
        sx={{ borderRadius: '10px', px: 3, minHeight: 44 }}
      >
        {isEdit ? 'Update' : 'Save'}
      </PrimaryButton>
    </Box>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={isEdit ? 'Edit Expense' : 'Add Expense'}
      subtitle="Required fields marked with *"
      maxWidth="md"
      footer={footer}
      mobileFullScreen
    >
      <form onSubmit={handleSubmit} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* --- Required Fields --- */}

          {/* Category */}
          <FormControl fullWidth error={Boolean(errors.categoryId)}>
            <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary } }}>
              Category *
            </InputLabel>
            <Select
              value={form.categoryId}
              label="Category *"
              onChange={(e) => handleChange('categoryId', e.target.value)}
              sx={selectSx}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {generalCategories.length > 0 && (
                <ListSubheader
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: theme.gray?.[500] || '#666',
                    backgroundColor: theme.background,
                    lineHeight: '32px',
                  }}
                >
                  General
                </ListSubheader>
              )}
              {generalCategories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
              {staffCategories.length > 0 && (
                <ListSubheader
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: theme.gray?.[500] || '#666',
                    backgroundColor: theme.background,
                    lineHeight: '32px',
                  }}
                >
                  Staff
                </ListSubheader>
              )}
              {staffCategories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
              {/* Restricted finance-only stewards cannot create categories
                  (backend POST /api/vendor/finance/categories is vendor-only).
                  Hide the "+ Add Category" affordance to keep the menu clean. */}
              {!isFinanceOnlyRestricted && (
                <MenuItem
                  value="__add__"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const name = prompt('Enter new expense category name:');
                    if (name && name.trim()) {
                      try {
                        const res = await financeApi.createCategory({ name: name.trim() });
                        if (onCategoryCreated) onCategoryCreated();
                        handleChange('categoryId', res.data.id);
                      } catch (err: any) {
                        alert(err?.response?.data?.message || 'Failed to create category');
                      }
                    }
                  }}
                  sx={{ color: theme.primary || 'var(--action)', fontWeight: 600, borderTop: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`, mt: 0.5 }}
                >
                  + Add Category
                </MenuItem>
              )}
            </Select>
            {errors.categoryId && (
              <Typography variant="caption" sx={{ color: theme.danger, mt: 0.5, ml: 1.5 }}>
                {errors.categoryId}
              </Typography>
            )}
          </FormControl>

          {/* Amount — large and prominent */}
          <TextField
            label="Amount *"
            type="number"
            value={form.amount}
            onChange={(e) => handleChange('amount', e.target.value ? Number(e.target.value) : '')}
            error={Boolean(errors.amount)}
            helperText={errors.amount}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '1.1rem' }}>
                    {CURRENCY.symbol}
                  </Typography>
                </InputAdornment>
              ),
              sx: { fontSize: '1.25rem', fontWeight: 600 },
            }}
            sx={{
              ...inputSx,
              '& .MuiOutlinedInput-root': {
                ...inputSx['& .MuiOutlinedInput-root'],
                minHeight: 56,
              },
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />

          {/* Single-tax field (e.g. UK VAT). Hidden for multi-part GST countries. */}
          {singleTax && (
          <TextField
            label={singleTaxLabel(taxSublabels)}
            type="number"
            value={form.vatAmount ?? ''}
            onChange={(e) => handleChange('vatAmount', e.target.value ? Number(e.target.value) : '')}
            fullWidth
            placeholder="0.00"
            helperText="Manual override — leave blank if not applicable"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 600, color: theme.primary, fontSize: '0.95rem' }}>
                    {CURRENCY.symbol}
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
            inputProps={{ min: 0, step: 0.01 }}
          />
          )}

          {/* Dates: From (required), To (optional range), Recorded (defaults to today).
              Stacks on mobile, side-by-side from sm+ to keep the form compact. */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              label="From Date *"
              type="date"
              value={form.fromDate}
              onChange={(e) => handleChange('fromDate', e.target.value)}
              error={Boolean(errors.fromDate)}
              helperText={errors.fromDate}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
            <TextField
              label="To Date"
              type="date"
              value={form.toDate ?? ''}
              onChange={(e) => handleChange('toDate', e.target.value)}
              error={Boolean(errors.toDate)}
              helperText={errors.toDate || 'Leave blank for single day'}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
            <TextField
              label="Recorded Date"
              type="date"
              value={form.recordedDate ?? ''}
              onChange={(e) => handleChange('recordedDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Defaults to today"
              sx={inputSx}
            />
          </Box>

          {/* Payment Status — chips */}
          <Box>
            <Typography
              variant="caption"
              sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, mb: 1, display: 'block', ml: 0.5 }}
            >
              Payment Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {PAYMENT_STATUSES.map((status) => {
                const isActive = form.paymentStatus === status.value;
                let chipColor = theme.gray?.[500] || '#666';
                let chipBg = theme.gray?.[100] || '#F0F0F0';
                if (status.value === 'PAID' && isActive) {
                  chipColor = theme.success || '#4CAF50';
                  chipBg = (theme.success || '#4CAF50') + '20';
                } else if (status.value === 'UNPAID' && isActive) {
                  chipColor = theme.danger || '#D99982';
                  chipBg = (theme.danger || '#D99982') + '20';
                } else if (status.value === 'PARTIALLY_PAID' && isActive) {
                  chipColor = theme.warning || '#FFC107';
                  chipBg = (theme.warning || '#FFC107') + '20';
                }
                return (
                  <Chip
                    key={status.value}
                    label={status.label}
                    onClick={() => handleChange('paymentStatus', status.value)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.813rem',
                      minHeight: 36,
                      px: 0.5,
                      backgroundColor: chipBg,
                      color: chipColor,
                      border: isActive ? `1.5px solid ${chipColor}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Payment Mode — hidden when status=UNPAID (no money moved yet) */}
          <Collapse in={showPaymentMode}>
            <PaymentModeSelect
              value={form.paymentMode}
              onChange={(v) => handleChange('paymentMode', v)}
              label="Payment Mode"
              fullWidth
            />
          </Collapse>

          {/* Due Date — conditional: only for UNPAID / PARTIALLY_PAID */}
          <Collapse in={showDueDate}>
            <TextField
              label="Due Date"
              type="date"
              value={form.dueDate}
              onChange={(e) => handleChange('dueDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Collapse>

          {/* Salary Month — for staff categories, the payroll period this payment covers */}
          <Collapse in={isStaffCategory}>
            <TextField
              label="Salary Month (for which month) *"
              type="month"
              value={form.salaryMonth ?? ''}
              onChange={(e) => handleChange('salaryMonth', e.target.value)}
              error={Boolean(errors.salaryMonth)}
              helperText={errors.salaryMonth || 'Used for payroll reports — e.g. April salary paid on May 3 is tagged 2026-04'}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Collapse>

          {/* Staff Member — conditional: only for staff categories */}
          <Collapse in={isStaffCategory && staffMembers.length > 0}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary } }}>
                Staff Member
              </InputLabel>
              <Select
                value={form.staffMemberId}
                label="Staff Member"
                onChange={(e) => handleChange('staffMemberId', e.target.value)}
                sx={selectSx}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {staffMembers.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name}
                    {member.role && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: theme.gray?.[500] || '#666' }}
                      >
                        ({member.role})
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Collapse>

          {/* --- Progressive Disclosure Toggle --- */}
          <Button
            onClick={() => setShowMore((prev) => !prev)}
            startIcon={
              showMore ? (
                <ChevronUpIcon style={{ width: 18, height: 18 }} />
              ) : (
                <ChevronDownIcon style={{ width: 18, height: 18 }} />
              )
            }
            sx={{
              textTransform: 'none',
              color: theme.primary,
              fontWeight: 600,
              fontSize: '0.875rem',
              justifyContent: 'flex-start',
              px: 1,
              py: 0.5,
              minHeight: 44,
              '&:hover': { backgroundColor: `${theme.primary}11` },
              '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
            }}
          >
            {showMore ? 'Less details' : 'More details'}
          </Button>

          {/* --- Expandable Optional Fields --- */}
          <Collapse in={showMore}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Supplier Name */}
              <Box>
                <Autocomplete
                  freeSolo
                  options={supplierOptions}
                  value={form.supplierName}
                  onInputChange={(_, val) => handleSupplierChange(val)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supplier Name"
                      placeholder="e.g. Metro Cash & Carry"
                      sx={inputSx}
                    />
                  )}
                  sx={{ '& .MuiAutocomplete-popupIndicator': { color: theme.gray?.[400] || '#999' } }}
                />
                {ruleHint && form.categoryId !== ruleHint.categoryId && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, ml: 0.5, display: 'block', fontSize: '0.75rem', color: theme.primary }}
                  >
                    Based on your rules, this supplier maps to &ldquo;{ruleHint.categoryName}&rdquo;{' '}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: theme.primary,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': { opacity: 0.8 },
                      }}
                      onClick={() => handleChange('categoryId', ruleHint.categoryId)}
                    >
                      Apply
                    </Typography>
                  </Typography>
                )}
              </Box>

              {/* Description / Notes */}
              <TextField
                label="Notes"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder="Optional description or reference number"
                sx={inputSx}
              />

              {/* Invoice / receipt image upload \u2014 uses image-gateway Worker.
                  entityId=0 for new expenses (path `v/{vendorId}/expenses/0/...`)
                  is allowed by the Worker; backend stores key, key survives expense save. */}
              {user?.id ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'var(--text-primary)' }}>
                    Invoice / Receipt
                  </Typography>
                  <MenuItemImageUpload
                    vendorId={user.id}
                    type="expenses"
                    entityId={form.id ?? 0}
                    imageKey={form.imageKey}
                    imageUrl={form.imageKey ? `${IMAGE_GATEWAY_URL}/${form.imageKey}` : form.imageUrl}
                    onImageUploaded={(key) => handleChange('imageKey', key)}
                    onImageRemoved={() => handleChange('imageKey', undefined)}
                  />
                </Box>
              ) : null}

              {/* Multi-part GST (e.g. India). Single-tax countries use the VAT field above. */}
              {!singleTax && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  border: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
                  backgroundColor: `${theme.primary}04`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.text,
                    fontSize: '0.875rem',
                    mb: 1.5,
                  }}
                >
                  Tax Details ({taxLabel})
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <TextField
                    label={taxSublabels[0] || 'CGST'}
                    type="number"
                    size="small"
                    value={form.cgst ?? ''}
                    onChange={(e) => handleChange('cgst', e.target.value ? Number(e.target.value) : '')}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontWeight: 600, color: theme.primary, fontSize: '0.85rem' }}>
                            {CURRENCY.symbol}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  <TextField
                    label={taxSublabels[1] || 'SGST'}
                    type="number"
                    size="small"
                    value={form.sgst ?? ''}
                    onChange={(e) => handleChange('sgst', e.target.value ? Number(e.target.value) : '')}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontWeight: 600, color: theme.primary, fontSize: '0.85rem' }}>
                            {CURRENCY.symbol}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  <TextField
                    label={taxSublabels[2] || 'IGST'}
                    type="number"
                    size="small"
                    value={form.igst ?? ''}
                    onChange={(e) => handleChange('igst', e.target.value ? Number(e.target.value) : '')}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontWeight: 600, color: theme.primary, fontSize: '0.85rem' }}>
                            {CURRENCY.symbol}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>

                {/* Total tax display */}
                {((Number(form.cgst) || 0) + (Number(form.sgst) || 0) + (Number(form.igst) || 0)) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: theme.primary,
                        fontSize: '0.78rem',
                      }}
                    >
                      Total Tax: {formatAmount((Number(form.cgst) || 0) + (Number(form.sgst) || 0) + (Number(form.igst) || 0), { showDecimals: false })}
                    </Typography>
                  </Box>
                )}
              </Box>
              )}
            </Box>
          </Collapse>

          {/* --- Repeat / Recurring Section (new expenses only; vendor-only feature) --- */}
          {!isEdit && !isFinanceOnlyRestricted && (
            <>
              <Button
                onClick={() => setShowRepeat((prev) => !prev)}
                startIcon={
                  <ArrowPathIcon style={{ width: 18, height: 18 }} />
                }
                sx={{
                  textTransform: 'none',
                  color: repeatFrequency !== 'NONE' ? (theme.success || '#4CAF50') : (theme.primary),
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  justifyContent: 'flex-start',
                  px: 1,
                  py: 0.5,
                  minHeight: 44,
                  '&:hover': { backgroundColor: `${theme.primary}11` },
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                }}
              >
                {repeatFrequency !== 'NONE'
                  ? `Repeats ${repeatFrequency === 'WEEKLY' ? 'Weekly' : repeatFrequency === 'BI_WEEKLY' ? 'Bi-weekly' : 'Monthly'}`
                  : 'Set up repeat'}
              </Button>

              <Collapse in={showRepeat}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 1.5,
                  borderRadius: '10px',
                  border: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
                  backgroundColor: `${theme.primary}05`,
                }}>
                  {/* Frequency chips */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, mb: 1, display: 'block', ml: 0.5 }}
                    >
                      Frequency
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {([
                        { value: 'NONE' as RepeatFrequency, label: 'None' },
                        { value: 'WEEKLY' as RepeatFrequency, label: 'Weekly' },
                        { value: 'BI_WEEKLY' as RepeatFrequency, label: 'Bi-weekly' },
                        { value: 'MONTHLY' as RepeatFrequency, label: 'Monthly' },
                      ]).map((opt) => {
                        const isActive = repeatFrequency === opt.value;
                        return (
                          <Chip
                            key={opt.value}
                            label={opt.label}
                            onClick={() => setRepeatFrequency(opt.value)}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.813rem',
                              minHeight: 36,
                              px: 0.5,
                              backgroundColor: isActive ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                              color: isActive ? theme.primary : theme.gray?.[500] || '#666',
                              border: isActive ? `1.5px solid ${theme.primary}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.85 },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Repeat day input */}
                  {repeatFrequency !== 'NONE' && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, mb: 0.5, display: 'block', ml: 0.5 }}
                      >
                        {repeatFrequency === 'MONTHLY' ? 'Day of month (1-31)' :
                         repeatFrequency === 'WEEKLY' || repeatFrequency === 'BI_WEEKLY' ? 'Day of week (1=Mon, 7=Sun)' : 'Repeat day'}
                      </Typography>
                      <TextField
                        type="number"
                        value={repeatDay}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (repeatFrequency === 'MONTHLY') {
                            setRepeatDay(Math.max(1, Math.min(31, val || 1)));
                          } else {
                            setRepeatDay(Math.max(1, Math.min(7, val || 1)));
                          }
                        }}
                        size="small"
                        inputProps={{
                          min: 1,
                          max: repeatFrequency === 'MONTHLY' ? 31 : 7,
                        }}
                        sx={{ ...inputSx, maxWidth: 120 }}
                      />
                      {repeatFrequency !== 'MONTHLY' && (
                        <Typography
                          variant="caption"
                          sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.68rem', mt: 0.5, display: 'block', ml: 0.5 }}
                        >
                          {['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][repeatDay] || ''}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </Box>
      </form>
    </Modal>
  );
};

export default ExpenseForm;
